<template>
  <div class="q-pa-md full-form">
    <Carousel
      v-model="value"
      :altText="altText"
      :isAdmin="isAdmin"
      @update:altText="val => $emit('update:altText', val)"
      @update:validateValue="val => $emit('update:validateValue', val)"
      v-if="nodeValue?.type === 'carousel'"
      ref="messageForm"
    />
    <Bubble
      v-model="nodeValue"
      :altText="altText"
      :isParent="value.type === 'bubble'"
      :isAdmin="isAdmin"
      @update:altText="val => $emit('update:altText', val)"
      @update:validateValue="val => $emit('update:validateValue', val)"
      v-if="nodeValue?.type === 'bubble'"
      ref="messageForm"
    />
    <Block
      v-model="nodeValue"
      :block="block"
      @update:validateValue="val => $emit('update:validateValue', val)"
      v-if="block"
    />
    <Box
      v-model="nodeValue"
      @update:validateValue="val => $emit('update:validateValue', val)"
      v-if="nodeValue?.type === 'box'"
      ref="messageForm"
    />
    <Button
      v-model="nodeValue"
      :isAdmin="isAdmin"
      @update:validateValue="val => $emit('update:validateValue', val)"
      v-if="nodeValue?.type === 'button'"
      ref="messageForm"
    />
    <Image
      v-model="nodeValue"
      :isAdmin="isAdmin"
      @update:validateValue="val => $emit('update:validateValue', val)"
      v-if="nodeValue?.type === 'image'"
      ref="messageForm"
    />
    <Icon
      v-model="nodeValue"
      :isAdmin="isAdmin"
      @update:validateValue="val => $emit('update:validateValue', val)"
      v-if="nodeValue?.type === 'icon'"
      ref="messageForm"
    />
    <Text
      v-model="nodeValue"
      :isAdmin="isAdmin"
      @update:validateValue="val => $emit('update:validateValue', val)"
      v-if="nodeValue?.type === 'text'"
      ref="messageForm"
    />
    <Span
      v-model="nodeValue"
      :isAdmin="isAdmin"
      @update:validateValue="val => $emit('update:validateValue', val)"
      v-if="nodeValue?.type === 'span'"
      ref="messageForm"
    />
    <Filler
      v-model="nodeValue"
      v-if="nodeValue?.type === 'filler'"
    />
    <Separator
      v-model="nodeValue"
      @update:validateValue="val => $emit('update:validateValue', val)"
      v-if="nodeValue?.type === 'separator'"
      ref="messageForm"
    />
    <Video
      v-model="nodeValue"
      :isAdmin="isAdmin"
      @update:validateValue="val => $emit('update:validateValue', val)"
      v-if="nodeValue?.type === 'video'"
      ref="messageForm"
    />
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { maska } from 'maska'
import { Prop } from 'vue-property-decorator'
import { IFlexMessageBubble, IFlexMessageBubbleStyle, IFlexMessageCarousel } from '@/utils/types'
import Carousel from '@/components/common/flex-message/forms/Carousel.vue'
import cloneDeep from 'lodash/cloneDeep'
import Bubble from '@/components/common/flex-message/forms/Bubble.vue'
import Block from '@/components/common/flex-message/forms/Block.vue'
import Text from '@/components/common/flex-message/forms/Text.vue'
import Span from '@/components/common/flex-message/forms/Span.vue'
import Box from '@/components/common/flex-message/forms/Box.vue'
import Filler from '@/components/common/flex-message/forms/Filler.vue'
import Separator from '@/components/common/flex-message/forms/Separator.vue'
import Button from '@/components/common/flex-message/forms/Button.vue'
import Image from '@/components/common/flex-message/forms/Image.vue'
import Icon from '@/components/common/flex-message/forms/Icon.vue'
import Video from '@/components/common/flex-message/forms/Video.vue'

@Options({
  components: { Video, Icon, Image, Button, Separator, Filler, Box, Span, Text, Block, Bubble, Carousel },
  directives: { maska },
  emits: ['update:modelValue', 'update:altText', 'update:validateValue'],
})
export default class FormPanel extends Vue {
  @Prop()
  modelValue!: IFlexMessageBubble | IFlexMessageCarousel

  @Prop()
  altText!: string

  @Prop()
  nodePath!: []

  @Prop({ default: '' })
  selectedPath!: string

  @Prop()
  errorPath!: string[]

  @Prop()
  isAdmin!: boolean

  block = ''

  blockStyle: IFlexMessageBubbleStyle = {
    header: {},
    hero: {},
    body: {},
    footer: {},
  }

  get value() {
    return this.modelValue
  }

  set value(value: IFlexMessageBubble | IFlexMessageCarousel) {
    this.$emit('update:modelValue', value)
  }

  get nodeValue() {
    this.block = ''
    if (this.selectedPath === '') return undefined
    const keys = cloneDeep(this.nodePath)
    const lastKey = keys[keys.length - 1]
    if (this.value.type === 'bubble') {
      if (lastKey === undefined) {
        return this.value
      } else if (!['header', 'hero', 'body', 'footer'].includes(lastKey)) {
        keys.pop()
      }
    } else {
      if (lastKey === 'carousel') {
        return this.value
      } else if (['box', 'image', 'text', 'button', 'filler', 'separator', 'video', 'span', 'icon'].includes(lastKey)) {
        keys.pop()
      }
    }
    if (['header', 'hero', 'body', 'footer'].includes(lastKey)) {
      this.block = lastKey
      if (this.value.type === 'bubble') {
        return this.initBubbleStyles(this.value)
      } else {
        keys.pop()
        const parent = keys.reduce((acc, key) => acc && acc[key], this.value)
        return this.initBubbleStyles(parent)
      }
    }
    console.log(keys.reduce((acc, key) => acc && acc[key], this.value))
    return keys.reduce((acc, key) => acc && acc[key], this.value)
  }

  initBubbleStyles(value: IFlexMessageBubble) {
    value.styles = { ...cloneDeep(this.blockStyle), ...value.styles }
    return value.styles
  }

  async validate() {
    if (this.nodeValue && 'type' in this.nodeValue) {
      return await this.$refs.messageForm.validate()
    }
    return true
  }
}
</script>

<style scoped lang="scss">
.full-form {
  height: calc(100vh - 120px);
  overflow-y: auto;
}
.title-plus .full-form {
  height: calc(100vh - 175px);
}
@media screen and (min-width: 1024px) {
  .full-form {
    height: calc(100vh - 175px);
  }
  .title-plus .full-form {
    height: calc(100vh - 220px);
  }
}
</style>
